// Turn a failed /api/payments/previewInvoiceWithoutOrder call into WHERE it should be
// shown and WHAT it should say. Pure — no React, no I/O — so every branch is unit
// testable without mounting the checkout.
//
// The backend (PublicOrdersApiServiceImpl.previewInvoiceWithoutOrder) refuses to
// price a cart with a 499 whose body is CheckoutErrorDto since backend commit
// 3fcdf62d51 (production 2026-09-09):
//
//   { errorType: 'ITEMS_NOT_AVAILABLE' | 'EMPTY_CART' | 'OUT_OF_STOCK',
//     transient: boolean,            // declared by the SERVER, never inferred here
//     message:   string,             // the historic plain-text body, verbatim
//     offerIds:  string[] }          // cart keys (region / empty cart) or ItemType
//                                    // enum names (stock)
//
// `transient` is the load-bearing field: true means the same cart is worth
// retrying later (today: only a stock shortfall), false means waiting changes
// nothing (the region does not sell the item; the cart priced nothing). We never
// derive it from the error name — a class added later that forgets to declare
// itself must read as permanent, not as restockable inventory.
//
// Before that commit the 499 body was bare prose, and the component matched on
// the English — which broke silently the day the stock message was reworded. A
// string body is therefore NOT classified any more: it lands in the generic cart
// slot with the sentence shown verbatim, so a backend rollback degrades to
// "something in your cart cannot be priced" rather than to a wrong slot.

/** Where a failure is rendered. */
export const PREVIEW_FAILURE_SLOT = Object.freeze({
  /** the shipping-country picker + the summary card ("not sold where you are") */
  REGION: 'region',
  /** the affected cart line in the summary card */
  STOCK: 'stock',
  /** the summary card, about the cart as a whole */
  CART: 'cart',
  /** the summary card, about us (timeout / unreachable / 5xx) */
  TRANSPORT: 'transport',
})

// The backend's own status codes (PublicOrdersResponseStatuses).
const SHIPPING_NOT_SUPPORTED = 498
const ITEMS_NOT_AVAILABLE = 499

// Copy. Each class says what is wrong AND what the customer can do about it; the
// retry sentence is appended ONLY when the server declared the failure transient.
export const PREVIEW_FAILURE_COPY = Object.freeze({
  REGION_ITEMS: 'Not available for delivery to your shipping country.',
  REGION_SHIPPING: 'We cannot ship to the selected country yet. Please choose a different shipping country.',
  STOCK: 'Out of stock for your shipping country.',
  EMPTY_CART: 'There are no items in your cart to order. Please add an item and try again.',
  CART_GENERIC: 'We could not price the items in your cart. Please review your cart and try again.',
  TRANSPORT_TIMEOUT: 'We could not price your cart in time.',
  TRANSPORT_UNREACHABLE: 'We could not load your order summary.',
  // Appended for a transient failure — the ONE case where "try again" is honest.
  RETRY: 'Please try again in a moment.',
  // Appended for a permanent region failure — waiting does not help, acting does.
  REGION_ACTION: 'Remove it from your cart or choose another shipping country.',
})

/**
 * Peel a JSON string literal (bounded). The Next proxy relays a plain-text backend
 * body through jsonResponse(), which JSON.stringify()s it; utils/clientHttp parses
 * it back for us today, but that is incidental, not a contract, so a quoted body
 * must never reach a customer with its quotes on.
 * @param {string} text
 * @returns {string}
 */
function peelJsonString(text) {
  let message = text.trim()
  for (let i = 0; i < 3 && message.length > 1 && message.startsWith('"') && message.endsWith('"'); i++) {
    let decoded
    try {
      decoded = JSON.parse(message)
    } catch (e) {
      break
    }
    if (typeof decoded !== 'string') break
    message = decoded.trim()
  }
  return message
}

/**
 * @param {any} data
 * @returns {string|null} a non-empty string from a shaped body, else null
 */
function messageOf(data) {
  if (typeof data === 'string') {
    const peeled = peelJsonString(data)
    return peeled || null
  }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const candidate = [data.message, data.error].find((v) => typeof v === 'string' && v.trim())
    return candidate ? candidate.trim() : null
  }
  return null
}

/**
 * @typedef {Object} PreviewFailure
 * @property {'region'|'stock'|'cart'|'transport'} slot  where to render it
 * @property {string|null} errorType   the backend's CheckoutErrorType name, when it sent one
 * @property {boolean} transient       true ONLY when the server declared it so (or the
 *                                     failure is ours — a timeout / unreachable proxy)
 * @property {string} message          the customer-facing sentence(s)
 * @property {string|null} detail      the backend's own message, verbatim (names the item)
 * @property {string[]} offerIds       what was rejected: cart keys, or ItemType names for stock
 * @property {number|null} status      the HTTP status, null when there was no response
 */

/**
 * Classify a rejected preview call.
 *
 * @param {any} error  the rejection from utils/clientHttp — `error.response` is
 *                     `{ status, data }` for a non-2xx and UNDEFINED for a network
 *                     error / abort (the 1dbe5854 guard: never read it unguarded)
 * @returns {PreviewFailure}
 */
export function classifyPreviewFailure(error) {
  const response = error && error.response ? error.response : null
  const status = response && typeof response.status === 'number' ? response.status : null
  const data = response ? response.data : undefined

  // No response at all: the request never completed (offline, aborted, DNS).
  if (!response) {
    return {
      slot: PREVIEW_FAILURE_SLOT.TRANSPORT,
      errorType: null,
      transient: true,
      message: `${PREVIEW_FAILURE_COPY.TRANSPORT_UNREACHABLE} ${PREVIEW_FAILURE_COPY.RETRY}`,
      detail: null,
      offerIds: [],
      status: null,
    }
  }

  if (status === SHIPPING_NOT_SUPPORTED) {
    // Still a bare prose body on the backend ("Shipping not supported"): no
    // fulfilment store for this country at all.
    return {
      slot: PREVIEW_FAILURE_SLOT.REGION,
      errorType: null,
      transient: false,
      message: PREVIEW_FAILURE_COPY.REGION_SHIPPING,
      detail: messageOf(data),
      offerIds: [],
      status,
    }
  }

  if (status === ITEMS_NOT_AVAILABLE) {
    const structured = data && typeof data === 'object' && !Array.isArray(data) && typeof data.errorType === 'string'
    const detail = messageOf(data)

    if (!structured) {
      // Pre-discriminator body (or a rollback): show the sentence, do not guess the class.
      return {
        slot: PREVIEW_FAILURE_SLOT.CART,
        errorType: null,
        transient: false,
        message: detail || PREVIEW_FAILURE_COPY.CART_GENERIC,
        detail,
        offerIds: [],
        status,
      }
    }

    const errorType = data.errorType
    // The server declares transience. Anything else — missing, a string, null —
    // reads as permanent.
    const transient = data.transient === true
    const offerIds = Array.isArray(data.offerIds) ? data.offerIds.filter((id) => typeof id === 'string') : []

    switch (errorType) {
      case 'ITEMS_NOT_AVAILABLE':
        return {
          slot: PREVIEW_FAILURE_SLOT.REGION,
          errorType,
          transient,
          message: withFollowUp(PREVIEW_FAILURE_COPY.REGION_ITEMS, transient, PREVIEW_FAILURE_COPY.REGION_ACTION),
          detail,
          offerIds,
          status,
        }
      case 'OUT_OF_STOCK':
        return {
          slot: PREVIEW_FAILURE_SLOT.STOCK,
          errorType,
          transient,
          message: withFollowUp(PREVIEW_FAILURE_COPY.STOCK, transient, null),
          detail,
          offerIds,
          status,
        }
      case 'EMPTY_CART':
        return {
          slot: PREVIEW_FAILURE_SLOT.CART,
          errorType,
          transient,
          message: withFollowUp(detail || PREVIEW_FAILURE_COPY.EMPTY_CART, transient, null),
          detail,
          offerIds,
          status,
        }
      default:
        // A class this build does not know. Honour the server's transient flag,
        // show its sentence, keep it at cart level.
        return {
          slot: PREVIEW_FAILURE_SLOT.CART,
          errorType,
          transient,
          message: withFollowUp(detail || PREVIEW_FAILURE_COPY.CART_GENERIC, transient, null),
          detail,
          offerIds,
          status,
        }
    }
  }

  // Our proxy's own shaped failures (route.js): 504 on a backend timeout, 502 when
  // the backend is unreachable. Both carry `{ error }`. Anything else (a 500 with
  // no body, an HTML error page) is treated the same way — ours, retryable.
  const timedOut = status === 504
  return {
    slot: PREVIEW_FAILURE_SLOT.TRANSPORT,
    errorType: null,
    transient: true,
    message: `${timedOut ? PREVIEW_FAILURE_COPY.TRANSPORT_TIMEOUT : PREVIEW_FAILURE_COPY.TRANSPORT_UNREACHABLE} ${PREVIEW_FAILURE_COPY.RETRY}`,
    detail: messageOf(data),
    offerIds: [],
    status,
  }
}

/**
 * @param {string} lead
 * @param {boolean} transient
 * @param {string|null} permanentAction  what to say when waiting will not help
 */
function withFollowUp(lead, transient, permanentAction) {
  if (transient) return `${lead} ${PREVIEW_FAILURE_COPY.RETRY}`
  return permanentAction ? `${lead} ${permanentAction}` : lead
}

/**
 * The cart lines a failure is about, resolved against the region's offers so the
 * summary can name them the way the shop does.
 *
 * - region / empty cart: `offerIds` are the CART KEYS (offer ids) → match `offer.id`.
 * - stock: `offerIds` are ItemType enum names → match `offer.type` (an offer's
 *   primary type). A bundle's secondary types are not on the client as enum names,
 *   so a shortfall inside a bundle may match nothing; the caller then falls back
 *   to the backend's `detail`, which names the item in prose.
 *
 * @param {PreviewFailure} failure
 * @param {Array<any>|null|undefined} offers   the region's offers (RegionOffers seam)
 * @returns {Set<string>} the offer ids in the cart that are affected
 */
export function affectedOfferIds(failure, offers) {
  const affected = new Set()
  if (!failure || !failure.offerIds || !failure.offerIds.length) return affected
  const list = Array.isArray(offers) ? offers : []
  failure.offerIds.forEach((rejected) => {
    if (failure.slot === PREVIEW_FAILURE_SLOT.STOCK) {
      list.filter((o) => o && o.type === rejected).forEach((o) => affected.add(o.id))
    } else {
      affected.add(rejected)
    }
  })
  return affected
}
